import { useCallback, useEffect, useState } from "react";
import { Lock, ShieldAlert } from "lucide-react";
import { C, inputStyle } from "./theme.js";
import * as vault from "./lib/keyvault.js";
import { fileUrl } from "./lib/api.js";

/* ============================================================================
   הצגת צילום מוצפן.

   הפענוח קורה בדפדפן. אם הכספת נעולה — מוצג כפתור, לא תמונה שבורה: זו
   הנקודה שבה בעל הנכס מזין את הסיסמה, והיא נשאלת רק כאן ורק כשבאמת
   פותחים צילום ת"ז.
   ============================================================================ */

export function SecureImage({ id, alt, className, style, zoom = false }) {
  const [url, setUrl] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | locked | error | plain
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setState("loading");
    try {
      const objectUrl = await vault.openEncryptedFile(id);
      if (objectUrl === null) {
        // קובץ ישן שנשמר לפני שההצפנה הופעלה.
        setState("plain");
        return;
      }
      setUrl(objectUrl);
      setState("idle");
    } catch (e) {
      if (e.message === "locked") setState("locked");
      else {
        setErr(e.message || "פענוח נכשל");
        setState("error");
      }
    }
  }, [id]);

  useEffect(() => {
    load();
    return vault.onVaultChange(load);
  }, [load]);

  // שחרור ה-object URL כדי לא לדלוף זיכרון בין צילומים.
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);

  if (!id) return <div className={className} style={{ ...style, background: "#EEF1EE" }} />;

  /* zoom — תצוגה מוגדלת בלחיצה. צילום ת"ז בגובה 40px אינו "תיעוד" של
     ממש; ההגדלה משתמשת באותו מקור שכבר בידינו (object URL מפוענח או
     הקובץ הגלוי), כך שקובץ מוצפן לא עובר שום מסלול נוסף. */
  const pic = (src) => {
    const img = <img src={src} alt={alt || ""} className={className}
      style={zoom ? { ...style, cursor: "zoom-in" } : style}
      onClick={zoom ? () => setOpen(true) : undefined} />;
    if (!zoom || !open) return img;
    return (
      <>
        {img}
        <div onClick={() => setOpen(false)} role="dialog" aria-label={alt || "צילום"}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(20,38,43,0.75)", cursor: "zoom-out" }}>
          <img src={src} alt={alt || ""} style={{ maxWidth: "94vw", maxHeight: "90vh", borderRadius: 12 }} />
        </div>
      </>
    );
  };

  if (state === "plain") return pic(fileUrl(id));

  if (state === "locked") {
    return (
      <button
        type="button"
        onClick={() => window.dispatchEvent(new CustomEvent("vault:unlock"))}
        className={className}
        style={{
          ...style, display: "flex", alignItems: "center", justifyContent: "center",
          gap: 4, background: "#EEF1EE", color: C.muted, fontSize: 11, border: "none",
        }}
        title="הצילום מוצפן. נפתח עם תעודת הזהות של בעל הנכס."
      >
        <Lock size={12} /> מוצפן
      </button>
    );
  }

  if (state === "error") {
    return (
      <div className={className} title={err}
        style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center", background: "#FBF2E3", color: "#6B4E12", fontSize: 11 }}>
        <ShieldAlert size={12} />
      </div>
    );
  }

  if (!url) return <div className={className} style={{ ...style, background: "#EEF1EE" }} />;

  return pic(url);
}

/* ========================================================================== */

/**
 * דיאלוג פתיחת הכספת. נפתח מאירוע גלובלי כדי שכל SecureImage תוכל לבקש
 * אותו בלי להעביר callbacks דרך חצי עץ הרכיבים.
 */
export function VaultUnlockDialog() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("secret"); // secret | recovery
  /* מה הכספת הזו מקבלת: ת"ז (החדש), סיסמה (כספות ישנות), או שתיהן —
     קובע רק את הניסוח; unlock מנסה את כל העטיפות הקיימות ממילא. */
  const [wraps, setWraps] = useState({ ownerId: true, password: false });
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const onAsk = () => {
      setOpen(true); setValue(""); setErr(""); setMode("secret");
      vault.wrappings().then(setWraps).catch(() => {});
    };
    window.addEventListener("vault:unlock", onAsk);
    return () => window.removeEventListener("vault:unlock", onAsk);
  }, []);

  if (!open) return null;

  const askId = wraps.ownerId || !wraps.password;
  const secretLabel = askId
    ? (wraps.password ? "תעודת הזהות של בעל הנכס (או הסיסמה הישנה)" : "תעודת הזהות של בעל הנכס")
    : "הסיסמה שאיתה הוקמה ההצפנה";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (mode === "secret") await vault.unlock(value);
      else await vault.unlockByRecovery(value);
      setOpen(false);
      setValue("");
    } catch (ex) {
      setErr(ex.message);
    }
    setBusy(false);
  };

  return (
    <div dir="rtl" className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(20,38,43,0.45)" }}>
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border p-6"
        style={{ background: C.card, borderColor: C.line }}>
        <div className="flex items-center gap-2 mb-1">
          <Lock size={16} style={{ color: C.teal }} />
          <h2 className="text-lg font-bold">פתיחת צילומי תעודות זהות</h2>
        </div>
        <p className="text-sm mb-4" style={{ color: C.muted }}>
          הצילומים מוצפנים במפתח שקיים רק בדפדפן שלכם. השרת אינו יכול לפתוח אותם.
        </p>

        {mode === "secret" && (
          <label className="text-[12px]" style={{ color: C.muted }}>{secretLabel}</label>
        )}
        <input
          type={mode === "secret" && !askId ? "password" : "text"}
          inputMode={mode === "secret" && askId && !wraps.password ? "numeric" : undefined}
          autoComplete={mode === "secret" && !askId ? "current-password" : "off"}
          autoFocus
          dir="ltr"
          value={value}
          onChange={(e) => { setValue(e.target.value); setErr(""); }}
          placeholder={mode === "secret" ? (askId ? "9 ספרות" : "") : "XXXX-XXXX-XXXX"}
          style={inputStyle}
        />

        {err && (
          <div className="mt-2 rounded-lg p-2.5 text-sm" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
            {err}
          </div>
        )}

        <button type="submit" disabled={busy || !value}
          className="mt-4 w-full rounded-xl py-3 text-sm"
          style={{ background: C.teal, color: "#fff", opacity: busy || !value ? 0.6 : 1 }}>
          {busy ? "פותח…" : "פתיחה"}
        </button>

        <button type="button" onClick={() => { setMode(mode === "secret" ? "recovery" : "secret"); setValue(""); setErr(""); }}
          className="mt-2 w-full rounded-xl py-2.5 text-sm"
          style={{ border: `1px solid ${C.line}`, color: C.muted }}>
          {mode === "secret" ? "פתיחה עם קוד השחזור" : askId ? "חזרה לתעודת זהות" : "חזרה לסיסמה"}
        </button>

        <button type="button" onClick={() => setOpen(false)}
          className="mt-2 w-full py-2 text-xs" style={{ color: C.muted }}>
          ביטול
        </button>
      </form>
    </div>
  );
}
